package ui

/**
 * Classifies the entity that owns a territory for map presentation.
 */
enum class TerritoryOwnerKind
{
    UNOWNED,
    LOCAL_PLAYER,
    REMOTE_PLAYER,
    NPC,
    UNKNOWN
}

/**
 * Contains the visual presentation resolved for a territory owner.
 *
 * A null [icon] tells the map renderer to retain the territory terrain icon.
 * A null [playerIndex] means the owner is not an active human player.
 *
 * @property ownerKind Classification of the resolved owner.
 * @property icon Human or NPC ownership icon, or null for a terrain icon.
 * @property ownerColor Border and fill color for the owner.
 * @property playerIndex Stable zero-based index in the active-player list.
 */
data class TerritoryOwnerPresentation(
    val ownerKind: TerritoryOwnerKind,
    val icon: String?,
    val ownerColor: String,
    val playerIndex: Int?
)

private const val NEUTRAL_OWNER_COLOR = "#AAAAAA"
private const val LOCAL_PLAYER_COLOR = "#FFD700"
private const val NPC_OWNER_COLOR = "#FF4500"

private val remotePlayerIcons = listOf("①", "②", "③", "④")
private val remotePlayerColors = listOf("#00FFFF", "#FF00FF", "#00FF00", "#FFA500")

/**
 * Resolves territory icon and color from stable active-player order.
 *
 * NPC entries in turn order are deliberately irrelevant. Human numbering is
 * defined exclusively by [activePlayerNames], preventing NPC turns from
 * shifting human owners beyond the numbered icon and color ranges.
 *
 * @param ownerName Name stored as the territory ruler.
 * @param localPlayerName Name of the player viewing the map.
 * @param activePlayerNames Human players in stable display order.
 * @param npcNames NPC names in the current world.
 * @return The resolved ownership presentation.
 */
fun resolveTerritoryOwnerPresentation(
    ownerName: String,
    localPlayerName: String,
    activePlayerNames: List<String>,
    npcNames: List<String>
): TerritoryOwnerPresentation
{
    if(ownerName.isBlank())
    {
        return TerritoryOwnerPresentation(
            ownerKind = TerritoryOwnerKind.UNOWNED,
            icon = null,
            ownerColor = NEUTRAL_OWNER_COLOR,
            playerIndex = null
        )
    }

    val playerIndex = activePlayerNames.indexOfFirst { playerName ->
        playerName.trim().equals(ownerName.trim(), ignoreCase = true)
    }

    if(playerIndex != -1)
    {
        val isLocalPlayer = ownerName.trim().equals(localPlayerName.trim(), ignoreCase = true)
        if(isLocalPlayer)
        {
            return TerritoryOwnerPresentation(
                ownerKind = TerritoryOwnerKind.LOCAL_PLAYER,
                icon = "👑",
                ownerColor = LOCAL_PLAYER_COLOR,
                playerIndex = playerIndex
            )
        }

        return TerritoryOwnerPresentation(
            ownerKind = TerritoryOwnerKind.REMOTE_PLAYER,
            icon = remotePlayerIcons.getOrNull(playerIndex) ?: "👤",
            ownerColor = remotePlayerColors.getOrNull(playerIndex) ?: remotePlayerColors.first(),
            playerIndex = playerIndex
        )
    }

    val isNpc = npcNames.any { npcName ->
        npcName.trim().equals(ownerName.trim(), ignoreCase = true)
    }
    if(isNpc)
    {
        return TerritoryOwnerPresentation(
            ownerKind = TerritoryOwnerKind.NPC,
            icon = "🤖",
            ownerColor = NPC_OWNER_COLOR,
            playerIndex = null
        )
    }

    return TerritoryOwnerPresentation(
        ownerKind = TerritoryOwnerKind.UNKNOWN,
        icon = null,
        ownerColor = NEUTRAL_OWNER_COLOR,
        playerIndex = null
    )
}
